import { MAX_RECIPES, validateRecipe, type MotionRecipe } from '../animation/procedural/recipes';
import { STORE_RECIPES, requestToPromise, runTransaction } from './db';
import { StorageError } from './errors';

export interface RecipeListing {
  recipes: MotionRecipe[];
  /** Records that cannot be read; reported, never silently deleted. */
  corrupted: { id: string; reason: string }[];
}

/**
 * Local storage for motion recipes (IndexedDB store `motionRecipes`). Every record is
 * validated on the way in and out; nothing leaves the browser.
 */
export class RecipeRepository {
  constructor(private readonly getDb: () => Promise<IDBDatabase>) {}

  async list(): Promise<RecipeListing> {
    const db = await this.getDb();
    const raw = await runTransaction(db, [STORE_RECIPES], 'readonly', (tx) => requestToPromise(tx.objectStore(STORE_RECIPES).getAll() as IDBRequest<unknown[]>));
    const recipes: MotionRecipe[] = [];
    const corrupted: RecipeListing['corrupted'] = [];
    for (const r of raw) {
      try {
        recipes.push(validateRecipe(r));
      } catch (e) {
        corrupted.push({ id: String((r as { id?: unknown })?.id ?? '?'), reason: e instanceof Error ? e.message : String(e) });
      }
    }
    recipes.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
    return { recipes, corrupted };
  }

  /** Inserts or replaces recipes in one transaction (all or nothing). */
  async save(...recipes: MotionRecipe[]): Promise<void> {
    const valid = recipes.map((r) => validateRecipe(r));
    const db = await this.getDb();
    await runTransaction(db, [STORE_RECIPES], 'readwrite', async (tx) => {
      const store = tx.objectStore(STORE_RECIPES);
      const keys = await requestToPromise(store.getAllKeys());
      const total = new Set([...keys.map(String), ...valid.map((r) => r.id)]).size;
      if (total > MAX_RECIPES) throw new StorageError('quota', `At most ${MAX_RECIPES} recipes can be saved.`);
      for (const r of valid) store.put(r);
    });
  }

  async delete(id: string): Promise<void> {
    const db = await this.getDb();
    await runTransaction(db, [STORE_RECIPES], 'readwrite', (tx) => {
      tx.objectStore(STORE_RECIPES).delete(id);
    });
  }
}
